import React, { useRef, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Drawer from '#component/drawer';
import MobileDrawer from '#component/drawer/mobile-view';
import AppBreadcrumb from '#component/breadcrumb';
import UserNotification from '#component/user/notification/notification';
import { whitelabel } from '#config/whitelabel';
import propTypes from './prop-types';
import {
  Account,
  AccountName,
  CompanyImgLogo,
  Logo,
  StyledAppBar,
  StyledToolbar,
  Timezone,
  UserNotificationsMob
} from './styles';
import { isWidth } from '#helper';

const ElevationScroll = ({ children }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0
  });

  return React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;

    return React.cloneElement(child, {
      elevation: trigger ? 5 : 0
    });
  });
};

function Header(props) {
  const { socket } = props;
  const appBarRef = useRef(null);
  const location = useLocation();
  const account = useSelector(s => s.account, shallowEqual);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.lg));

  useEffect(() => {
    window.appBarRef = appBarRef;
    return () => {
      window.appBarRef = null;
    };
  }, []);

  const getOffset = () => {
    const offset = new Date().getTimezoneOffset();
    return offset < 0 ? `UTC+${offset / -60}` : `UTC-${offset / 60}`;
  };

  const mainPage = account.defaultPage || '/dashboard';
  const showUserNotifications = Boolean(account.notificationMsg);

  return (
    <>
      <style type="text/css">
        {`
        .tec4app #header { background-color: #5eb676!important; color: #fff !important; }
        .tec4app #header nav + div { visibility: hidden; }
        .tec4app #header p, .tec4app #header a, .tec4app #header svg { color: #fff !important; }
        `}
      </style>

      <ElevationScroll>
        <StyledAppBar position="fixed" id="header" ref={appBarRef}>
          <StyledToolbar>
            {isMobile ? (
              <MobileDrawer account={account} socket={socket} />
            ) : (
              <Drawer account={account} socket={socket} />
            )}

            <AppBreadcrumb />

            {(!isWidth() || location.pathname === '/') && (
              <>
                <Logo>
                  <Link to={mainPage}>
                    <CompanyImgLogo
                      src={whitelabel.buildAssetUrl('media/logo-header.svg')}
                      alt="Company logo"
                    />
                  </Link>
                </Logo>

                {showUserNotifications && (
                  <span>
                    <UserNotification socket={socket} />
                  </span>
                )}

                <Account aria-label="account">
                  <div>
                    Hello,&nbsp;
                    <AccountName to={`/user/${account.id}`}>
                      {account.firstName}
                    </AccountName>
                    !
                  </div>

                  <Timezone>
                    {`${Intl.DateTimeFormat().resolvedOptions().timeZone} (${getOffset()})`}
                  </Timezone>
                </Account>
              </>
            )}

            {isWidth() && showUserNotifications && (
              <UserNotificationsMob>
                <UserNotification socket={socket} />
              </UserNotificationsMob>
            )}
          </StyledToolbar>
        </StyledAppBar>
      </ElevationScroll>

      <StyledToolbar />
    </>
  );
}

Header.propTypes = propTypes;

export default Header;
